import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";

export default function Labels() {
  const { id: batchId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(null);
  const [product, setProduct] = useState(null);
  const [config, setConfig] = useState(null);
  const [empresaActiva, setEmpresaActiva] = useState(null);
  const [labels, setLabels] = useState([]);
  const [savingPdf, setSavingPdf] = useState(false);
  const labelRefs = useRef({});

  useEffect(() => {
    fetchData();
  }, [batchId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [batchRes, empresaRes] = await Promise.all([
        api.get(`/batches/${batchId}`),
        api.get("/config/empresa-activa")
      ]);
      
      const batchData = batchRes.data;
      setBatch(batchData);
      setConfig(empresaRes.data);
      setEmpresaActiva(empresaRes.data.empresa);

      if (batchData.producto_id) {
        const productRes = await api.get(`/products/${batchData.producto_id}`);
        setProduct(productRes.data);
      }

      // Generar etiquetas SOLO para tallas que tienen datos
      if (batchData.tallas && batchData.tallas.length > 0) {
        const generatedLabels = batchData.tallas
          .filter(t => t.talla && (t.num_bultos > 0 || t.peso_por_bulto > 0))
          .map(t => ({
            talla: t.talla,
            peso: t.peso_por_bulto,
            bultos: t.num_bultos,
            empaque_seleccionado: t.empaque_seleccionado || null, // Empaque específico de esta talla
            id: `${t.talla}_${t.peso_por_bulto}`
          }));
        setLabels(generatedLabels);
      }
    } catch (error) {
      toast.error("Error al cargar los datos");
      navigate("/batches");
    } finally {
      setLoading(false);
    }
  };

  // Formatear fecha SIN conversión de zona horaria
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const datePart = dateStr.split('T')[0];
    const parts = datePart.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  };

  // Generar nombre del archivo según la fórmula:
  // AAAAMMDD NÚMEROFACTURAORIGEN CONTENEDOR/AWB ETIQUETA TALLA PESO
  // Usa la fecha de CREACIÓN del lote (created_at), no la fecha actual
  const generateFileName = (label) => {
    // Extraer fecha sin conversión de zona horaria
    const fechaStr = batch?.created_at?.split('T')[0] || new Date().toISOString().slice(0,10);
    const dateStr = fechaStr.replace(/-/g, '');
    const factura = batch?.factura_origen || batch?.numero_lote || '';
    const envio = batch?.contenedor || batch?.awb_aereo || '';
    return `${dateStr} ${factura} ${envio} ETIQUETA T${label.talla} ${label.peso}Kg`;
  };

  const handlePrintAll = () => {
    window.print();
  };

  // Guardar PDF individual para una etiqueta
  const saveLabelAsPdf = async (label, idx) => {
    const element = labelRefs.current[idx];
    if (!element) return;

    const filename = generateFileName(label);
    
    // Etiqueta grande horizontal en A4 (190mm ancho con márgenes de 10mm)
    const opt = {
      margin: [10, 10, 10, 10], // Márgenes normales A4
      filename: `${filename}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2, 
        useCORS: true,
        logging: false
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: 'avoid-all' }
    };

    try {
      // Generar el PDF blob
      const pdfBlob = await html2pdf().set(opt).from(element).outputPdf('blob');
      
      // Registrar en documentos del sistema
      // IMPORTANTE: Cada etiqueta tiene un tipo_documento único para evitar sobreescritura
      const formData = new FormData();
      formData.append("file", pdfBlob, `${filename}.pdf`);
      formData.append("contenedor", batch?.contenedor || "");
      formData.append("awb", batch?.awb_aereo || "");
      formData.append("bl", batch?.bl_maritimo || "");
      formData.append("numero_lote", batch?.numero_lote || "");
      // Tipo único por talla para que se guarden todas las etiquetas
      formData.append("tipo_documento", `etiqueta_T${label.talla}_${label.peso}kg`);
      formData.append("batch_id", batchId);
      formData.append("nombre_archivo", `${filename}.pdf`);

      await api.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      // Descargar localmente
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error("Error generando PDF:", error);
      return false;
    }
  };

  // Guardar todas las etiquetas como PDFs individuales
  const handleSaveAllPdfs = async () => {
    setSavingPdf(true);
    let successCount = 0;
    
    for (let i = 0; i < labels.length; i++) {
      const success = await saveLabelAsPdf(labels[i], i);
      if (success) successCount++;
      await new Promise(r => setTimeout(r, 500));
    }

    setSavingPdf(false);
    
    if (successCount === labels.length) {
      toast.success(`${successCount} etiquetas guardadas correctamente`);
    } else {
      toast.warning(`${successCount} de ${labels.length} etiquetas guardadas`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Lote no encontrado</p>
      </div>
    );
  }

  const productName = product?.nombre || batch.producto_nombre || "PULPO CONGELADO";
  const speciesName = product?.nombre_cientifico || batch.nombre_cientifico || "OCTOPUS VULGARIS";
  const empaqueDefault = batch.empaque || product?.empaque || "BLOQUE";
  
  // Función para obtener el empaque correcto para cada etiqueta
  const getEmpaqueForLabel = (label) => {
    return label.empaque_seleccionado || empaqueDefault;
  };

  return (
    <div className="labels-page">
      {/* Header - NO se imprime */}
      <div className="no-print p-4 bg-white border-b flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/batches")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Etiquetas - Lote {batch.numero_lote}</h1>
            <p className="text-sm text-slate-500">{labels.length} etiquetas a generar (solo tallas con datos)</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleSaveAllPdfs} 
            className="bg-slate-900 hover:bg-slate-800"
            disabled={savingPdf || labels.length === 0}
          >
            {savingPdf ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Guardar PDFs
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Lista de etiquetas */}
      <div className="labels-container p-4 print:p-0">
        {labels.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            No hay tallas con datos para este lote. Añade tallas para generar etiquetas.
          </div>
        ) : (
          <div className="space-y-6 print:space-y-0">
            {labels.map((label, idx) => (
              <div 
                key={idx}
                ref={el => labelRefs.current[idx] = el}
                className="label-card bg-white border-2 border-black mx-auto print:mx-0"
                style={{ 
                  width: '190mm', 
                  height: '145mm',
                  padding: '3mm',
                  pageBreakAfter: 'always',
                  boxSizing: 'border-box',
                  overflow: 'hidden'
                }}
              >
                {/* Estructura de etiqueta */}
                <div className="flex" style={{ height: '139mm' }}>
                  {/* Texto vertical izquierdo - COLUMNA DE LETRAS */}
                  <div 
                    className="flex flex-col items-center justify-center border-r-2 border-black"
                    style={{ 
                      width: '14mm',
                      minWidth: '14mm',
                      fontSize: '8px',
                      fontWeight: '800',
                      color: '#dc2626',
                      textTransform: 'uppercase',
                      backgroundColor: '#fee2e2',
                      textAlign: 'center',
                      lineHeight: '1.1',
                      letterSpacing: '0'
                    }}
                  >
                    {"NO CONGELAR UNA VEZ DESCONGELADO".split("").map((char, i) => (
                      <span key={i} style={{ display: 'block', height: char === " " ? '6px' : 'auto' }}>
                        {char === " " ? "" : char}
                      </span>
                    ))}
                  </div>

                  {/* Contenido principal */}
                  <div className="flex-1 flex flex-col" style={{ paddingLeft: '3mm' }}>
                    {/* Encabezado del producto */}
                    <div className="text-center border-b-2 border-black" style={{ padding: '2mm 0 3mm 0' }}>
                      <h2 style={{ fontSize: '28px', fontWeight: '900', margin: 0, letterSpacing: '1px' }}>{productName.toUpperCase()}</h2>
                      <p style={{ fontSize: '12px', margin: '2px 0 1px 0' }}>ESPECIE: {speciesName.toUpperCase()}</p>
                      <p style={{ fontSize: '12px', margin: 0 }}>EMPAQUE: {getEmpaqueForLabel(label).toUpperCase()}</p>
                    </div>

                    {/* INGREDIENTES/FECHAS + LOTE */}
                    <div className="flex border-b border-black" style={{ padding: '2mm 0' }}>
                      <div className="flex-1" style={{ fontSize: '10px', lineHeight: '1.4' }}>
                        <p style={{ margin: '0 0 1px 0' }}><span style={{ fontWeight: 'bold' }}>INGREDIENTES:</span> {speciesName.toUpperCase()}</p>
                        <p style={{ margin: '0 0 1px 0' }}><span style={{ fontWeight: 'bold' }}>FECHA DE CONGELACIÓN:</span> {formatDate(batch.fecha_congelacion)}</p>
                        <p style={{ margin: '0 0 1px 0' }}><span style={{ fontWeight: 'bold' }}>CONSUMIR PREFERENTEMENTE</span></p>
                        <p style={{ margin: 0 }}><span style={{ fontWeight: 'bold' }}>ANTES DEL:</span> {formatDate(batch.fecha_caducidad)}</p>
                      </div>
                      {/* LOTE - MUY GRANDE */}
                      <div className="text-right" style={{ paddingRight: '3mm', minWidth: '75mm' }}>
                        <p style={{ fontSize: '11px', color: '#333', margin: 0, fontWeight: 'bold' }}>LOTE</p>
                        <p style={{ fontSize: '32px', fontWeight: '900', margin: 0, letterSpacing: '1px', color: '#000' }}>{batch.numero_lote}</p>
                      </div>
                    </div>

                    {/* TALLA y PESO NETO */}
                    <div className="border-b border-black text-center" style={{ padding: '3mm 0' }}>
                      <p style={{ fontSize: '28px', fontWeight: '900', margin: 0, letterSpacing: '1px' }}>
                        TALLA: {label.talla} &nbsp;&nbsp;|&nbsp;&nbsp; PESO NETO: {label.peso} Kgs
                      </p>
                    </div>

                    {/* Información de producción - MÁS GRANDE */}
                    <div style={{ fontSize: '11px', lineHeight: '1.4', padding: '2mm 0', flex: '1' }}>
                      <p style={{ margin: '0 0 2px 0' }}><span style={{ fontWeight: 'bold' }}>Captura Silvestre:</span> {batch.zona_fao || product?.zona_fao || "Zona FAO31"}</p>
                      <p style={{ margin: '0 0 2px 0' }}><span style={{ fontWeight: 'bold' }}>Método de Producción:</span> Pesca Extractiva (Capturado).</p>
                      <p style={{ margin: '0 0 2px 0' }}><span style={{ fontWeight: 'bold' }}>Arte de Pesca:</span> {batch.arte_pesca || product?.arte_pesca || "Nasas (FPO)."}</p>
                      <p style={{ margin: '0 0 2px 0' }}><span style={{ fontWeight: 'bold' }}>País de Origen:</span> {batch.pais_origen || "Venezuela"}</p>
                      <p style={{ margin: '0 0 2px 0' }}><span style={{ fontWeight: 'bold' }}>Producido Por:</span> {config?.productor_nombre || "INVERSIONES FRISUR, C.A."} RIF {config?.productor_rif || "J-41253799-7"}</p>
                      <p style={{ margin: '0 0 2px 0' }}><span style={{ fontWeight: 'bold' }}>SPES:</span> {config?.productor_spes || "SPES-6479"}</p>
                      <p style={{ margin: '0 0 2px 0' }}><span style={{ fontWeight: 'bold' }}>Exportado Por:</span> LG GLOBAL SEAFOOD C.A. J-50731006-0</p>
                      <p style={{ margin: '0 0 2px 0' }}><span style={{ fontWeight: 'bold' }}>Importado Por:</span> {empresaActiva?.nombre || "VEXPA COMMODITIES ESPAÑA, SL"} {empresaActiva?.cif || "B72448970"}.</p>
                      <p style={{ margin: '0 0 2px 0', paddingLeft: '14mm' }}>{empresaActiva?.direccion || "C JOSE CHABAS BORDEHORE, NUM 3 46019 VALÈNCIA - (VALENCIA)"}</p>
                      <p style={{ margin: 0, paddingLeft: '14mm' }}>ESPAÑA RGSEAA: {empresaActiva?.rgseaa || "12.67890/VA"}</p>
                    </div>

                    {/* MANTENER CONGELADO */}
                    <div className="text-center" style={{ backgroundColor: '#d1d5db', padding: '5mm 0', marginTop: 'auto' }}>
                      <p style={{ fontSize: '24px', fontWeight: '900', margin: 0, letterSpacing: '1px' }}>MANTENER CONGELADO A -18ºC</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Estilos para impresión */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 10mm;
          }
          
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          html, body {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .label-card {
            width: 100mm !important;
            height: 100mm !important;
            margin: auto !important;
          }
          
          #emergent-badge,
          .no-print,
          .print-hide,
          [data-testid="app-sidebar"],
          .sidebar,
          aside {
            display: none !important;
          }
          
          .labels-page {
            display: block !important;
          }
          
          .labels-container {
            padding: 0 !important;
          }
          
          .labels-container > div {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .label-card {
            width: 100mm !important;
            min-height: 80mm !important;
            margin: 0 !important;
            page-break-after: always !important;
            break-after: page !important;
            border: 1px solid #000 !important;
            box-sizing: border-box !important;
          }
          
          .label-card:last-child {
            page-break-after: auto !important;
          }
        }
      `}</style>
    </div>
  );
}
